import { memo } from 'react';
import { Button, TabNav } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Check, LayoutGrid, Plus } from 'lucide-react';
import { useIntegrations } from '../context';
import { INTEGRATIONS_TABS } from '../constants';

/**
 * Pill styling measured from the prototype `.home-tab` (dark theme):
 *   idle   → transparent, text --t2 (white/60)
 *   active → bg --hover-strong (white/8), text --t1 (white/92), border --line (white/10)
 * Light-mode values approximate the same subtle-elevated look with theme tokens.
 */
const PILL_BASE = [
  'inline-flex h-[34px] items-center gap-2 rounded-[8px] border px-[14px]',
  'text-[13px] font-semibold whitespace-nowrap transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B7FFF]/50',
].join(' ');
const PILL_IDLE =
  'border-transparent text-content-secondary hover:text-content dark:text-white/60 dark:hover:text-white/80';
const PILL_ACTIVE =
  'border-edge bg-black/[0.04] text-content dark:border-white/10 dark:bg-white/[0.08] dark:text-white/90';

/** `.home-tab-count`: idle bg --hover (white/4) text --t2; active blue accent #2B7FFF @14%. */
const COUNT_BASE =
  'inline-flex h-[18px] min-w-[22px] items-center justify-center rounded-[9px] px-[6px] text-[10px] font-extrabold leading-none';
const COUNT_IDLE =
  'bg-black/[0.04] text-content-secondary dark:bg-white/[0.04] dark:text-white/60';
const COUNT_ACTIVE = 'bg-[rgba(43,127,255,0.14)] text-[#2B7FFF]';

/**
 * One catalog-lens pill (prototype `.home-tab`). Rendered inside `TabNav`, so it
 * carries `role="tab"` + `aria-selected` for the DS tablist semantics.
 *
 * @param {object} props
 * @param {boolean} props.selected
 * @param {() => void} props.onClick
 * @param {import('lucide-react').LucideIcon} props.icon
 * @param {string} props.label
 * @param {number} props.count
 */
function TabPill({ selected, onClick, icon: Icon, label, count }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={[PILL_BASE, selected ? PILL_ACTIVE : PILL_IDLE].join(' ')}
    >
      <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
      <span>{label}</span>
      <span className={[COUNT_BASE, selected ? COUNT_ACTIVE : COUNT_IDLE].join(' ')}>{count}</span>
    </button>
  );
}

/**
 * Tab strip below the Needs-Your-Attention band (CT-4158): switches between the
 * two catalog lenses and docks the Request Integration action on the right.
 *
 * The prototype `.home-tabs` uses discrete pills (only the active one is filled),
 * so this renders custom `TabPill`s wrapped in `TabNav` — reusing the DS tablist
 * semantics (`role="tablist"` + arrow-key navigation) while matching the pill look.
 * `activeTab` lives in `IntegrationsContext`; only the page content subtree is
 * keyed on it, so switching tabs never re-mounts the hero, band, or strip.
 */
function IntegrationsTabStrip() {
  const { t } = useTranslation('admin-studio');
  const {
    activeTab,
    setActiveTab,
    alreadyIntegratedCount,
    marketplaceCount,
    openRequestIntegration,
  } = useIntegrations();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <TabNav ariaLabel={t('integrations.home.tabs.ariaLabel')} className="!w-auto !items-center gap-1">
        <TabPill
          selected={activeTab === INTEGRATIONS_TABS.ALREADY_INTEGRATED}
          onClick={() => setActiveTab(INTEGRATIONS_TABS.ALREADY_INTEGRATED)}
          icon={Check}
          label={t('integrations.home.tabs.alreadyIntegrated')}
          count={alreadyIntegratedCount}
        />
        <TabPill
          selected={activeTab === INTEGRATIONS_TABS.MARKETPLACE}
          onClick={() => setActiveTab(INTEGRATIONS_TABS.MARKETPLACE)}
          icon={LayoutGrid}
          label={t('integrations.home.tabs.marketplace')}
          count={marketplaceCount}
        />
      </TabNav>

      <Button variant="classicPrimary" size="md" leftIcon={Plus} onClick={openRequestIntegration}>
        {t('integrations.home.tabs.requestAction')}
      </Button>
    </div>
  );
}

export default memo(IntegrationsTabStrip);
